'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import type { ActionResult } from '@/shared/types/actions';
import { SessionService } from '@/contexts/authentication';
import type {
  CharacterTemplate,
  CreateCharacterTemplateInput,
  UpdateCharacterTemplateInput,
} from '../types';

// ── Reads ──

/** Active templates for the story wizard (any authenticated family). */
export async function listActiveCharacterTemplates(): Promise<CharacterTemplate[]> {
  const familyId = await SessionService.getFamilyId();
  if (!familyId) return [];
  const { getCharacterTemplateRepository } = await import('@/contexts/characters/repositories');
  const { getLocale } = await import('next-intl/server');
  const { localizedTemplateName } = await import('../utils/systemCharacter');
  const locale = await getLocale().catch(() => 'en');
  const templates = await getCharacterTemplateRepository().listActiveTemplates();
  // Attach the deterministic seed id (matches seedCharactersFromTemplates) so the
  // wizard detects selection from characterIds alone — stable across remounts.
  // The name is shown to a parent, so it comes out in their language.
  return templates.map((t) => ({
    ...t,
    name: localizedTemplateName(t, locale),
    seededCharacterId: `seed_${familyId}_${t.id}`,
  }));
}

/** Every template (admin view). */
export async function listAllCharacterTemplates(): Promise<CharacterTemplate[]> {
  await SessionService.requireRole('admin');
  const { getCharacterTemplateRepository } = await import('@/contexts/characters/repositories');
  return getCharacterTemplateRepository().listAllTemplates();
}

// ── Writes (admin only) ──

export async function createCharacterTemplate(
  input: CreateCharacterTemplateInput,
): Promise<ActionResult<CharacterTemplate>> {
  try {
    await SessionService.requireRole('admin');
    if (!input.name?.trim()) return { success: false, error: 'Name is required' };
    const { getCharacterTemplateRepository } = await import('@/contexts/characters/repositories');
    const template = await getCharacterTemplateRepository().createTemplate({
      ...input,
      name: input.name.trim(),
    });
    return { success: true, data: template };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('createCharacterTemplate', e) };
  }
}

export async function updateCharacterTemplate(
  id: string,
  patch: UpdateCharacterTemplateInput,
): Promise<ActionResult<CharacterTemplate>> {
  try {
    await SessionService.requireRole('admin');
    const { getCharacterTemplateRepository } = await import('@/contexts/characters/repositories');
    const template = await getCharacterTemplateRepository().updateTemplate(id, patch);
    return { success: true, data: template };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('updateCharacterTemplate', e) };
  }
}

export async function deleteCharacterTemplate(id: string): Promise<ActionResult<{ id: string }>> {
  try {
    await SessionService.requireRole('admin');
    const { getCharacterTemplateRepository } = await import('@/contexts/characters/repositories');
    await getCharacterTemplateRepository().deleteTemplate(id);
    return { success: true, data: { id } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('deleteCharacterTemplate', e) };
  }
}

export async function reorderCharacterTemplates(
  idsInOrder: string[],
): Promise<ActionResult<{ ids: string[] }>> {
  try {
    await SessionService.requireRole('admin');
    const { getCharacterTemplateRepository } = await import('@/contexts/characters/repositories');
    await getCharacterTemplateRepository().reorderTemplates(idsInOrder);
    return { success: true, data: { ids: idsInOrder } };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('reorderCharacterTemplates', e) };
  }
}

/** Convenience toggle used by the admin list (thin wrapper over update). */
export async function setCharacterTemplateActive(
  id: string,
  isActive: boolean,
): Promise<ActionResult<CharacterTemplate>> {
  return updateCharacterTemplate(id, { isActive });
}
